import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { InputField } from "@/components/InputField/InputField";
import {
	MessageTemplate,
	UpdateMessageTemplateSchema,
	UpdateMessageTemplate,
} from "@/pages/Private/pages/Messages/schema/message-template.schema";
import { useUpdateMessageTemplateMutation } from "@/pages/Private/redux/messageTemplate/messageTemplate.api";

interface UpdateReplyTemplateModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	prompt: MessageTemplate;
}

export const UpdateReplyTemplateModal: FunctionComponent<UpdateReplyTemplateModalProps> = ({
	isOpen,
	setIsOpen,
	prompt,
}) => {
	const { t } = useTranslation();
	const [updateReplyTemplate, { isLoading, error }] = useUpdateMessageTemplateMutation();

	const [editorValue, setEditorValue] = useState<string>();

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<UpdateMessageTemplate>({
		defaultValues: {
			id: prompt.id,
			name: prompt.name,
			content: prompt.content,
		},
		resolver: zodResolver(UpdateMessageTemplateSchema),
	});

	const formValues = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: UpdateMessageTemplate) => {
		try {
			await updateReplyTemplate({
				...values,
			}).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="lg"
			submitButtonText={t("basics.confirm")}
			title={"Edit Prompt Template"}
		>
			<div className="flex flex-col w-full gap-2">
				<div className="flex items-center">
					<InputField
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("name", e.target.value);
						}}
						label={"Name"}
						name={"name"}
						placeholder={"e.g. My Prompt Template"}
						value={formValues.name || ""}
					/>
				</div>

				<div className="flex items-center">
					<InputField
						multiline
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("content", e.target.value);
						}}
						label={"Message"}
						name={"content"}
						placeholder={"e.g. My Prompt Template"}
						rows={4}
						value={formValues.content}
					/>
				</div>

				{renderErrorMessages(getAllErrors(error, formErrors))}
			</div>
		</Modal>
	);
};
